import { useState, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  DocumentArrowUpIcon,
  SparklesIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  DocumentTextIcon,
  CurrencyDollarIcon,
  TruckIcon,
  ClipboardDocumentListIcon,
} from '@heroicons/react/24/outline';
import { useDropzone } from 'react-dropzone';
import toast from 'react-hot-toast';
import { Modal } from './Modal';
import { documentsApi } from '../services/api';

interface ExtractedData {
  documentType: string;
  confidence: number;
  fields: Record<string, any>;
  suggestedActions: {
    action: string;
    description: string;
    autoApply: boolean;
  }[];
  warnings: {
    field: string;
    issue: string;
    severity: 'low' | 'medium' | 'high';
  }[];
  matchedPO?: {
    id: string;
    poNumber: string;
  };
}

interface DocumentAnalyzerProps {
  isOpen: boolean;
  onClose: () => void;
  onAnalysisComplete?: (data: ExtractedData) => void;
  defaultPoId?: string;
}

const DOCUMENT_TYPES = [
  { type: 'proforma', label: 'Proforma Invoice', icon: DocumentTextIcon },
  { type: 'commercial_invoice', label: 'Commercial Invoice', icon: CurrencyDollarIcon },
  { type: 'packing_list', label: 'Packing List', icon: ClipboardDocumentListIcon },
  { type: 'swift', label: 'SWIFT/Payment', icon: CurrencyDollarIcon },
  { type: 'permit', label: 'Permit (MOH/MOT)', icon: DocumentTextIcon },
  { type: 'bill_of_lading', label: 'Bill of Lading', icon: TruckIcon },
  { type: 'certificate_origin', label: 'Certificate of Origin', icon: DocumentTextIcon },
  { type: 'other', label: 'Other', icon: DocumentTextIcon },
];

export default function DocumentAnalyzer({
  isOpen,
  onClose,
  onAnalysisComplete,
  defaultPoId,
}: DocumentAnalyzerProps) {
  const queryClient = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [extractedData, setExtractedData] = useState<ExtractedData | null>(null);
  const [_isAnalyzing, setIsAnalyzing] = useState(false);
  const [step, setStep] = useState<'upload' | 'analyzing' | 'review'>('upload');

  // File upload handler
  const onDrop = useCallback((acceptedFiles: File[]) => {
    const uploadedFile = acceptedFiles[0];
    if (uploadedFile) {
      setFile(uploadedFile);

      // Create preview for images
      if (uploadedFile.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = () => setPreview(reader.result as string);
        reader.readAsDataURL(uploadedFile);
      } else if (uploadedFile.type === 'application/pdf') {
        setPreview('/pdf-icon.svg');
      }
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/*': ['.png', '.jpg', '.jpeg', '.webp'],
    },
    maxFiles: 1,
    maxSize: 20 * 1024 * 1024, // 20MB
  });

  // Upload and analyze mutation
  const analyzeMutation = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error('No file selected');

      setStep('analyzing');
      setIsAnalyzing(true);

      // Create form data
      const formData = new FormData();
      formData.append('file', file);
      if (defaultPoId) {
        formData.append('po_id', defaultPoId);
      }

      // Upload document
      const uploadResponse = await documentsApi.upload(formData);
      const documentId = uploadResponse.data.data.id;

      // Analyze with AI
      const analysisResponse = await documentsApi.analyze(documentId, 'auto');
      return analysisResponse.data.data as ExtractedData;
    },
    onSuccess: (data) => {
      setExtractedData(data);
      setStep('review');
      setIsAnalyzing(false);
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to analyze document');
      setIsAnalyzing(false);
      setStep('upload');
    },
  });

  // Apply suggested actions
  const applyActionMutation = useMutation({
    mutationFn: async (actionIndex: number) => {
      if (!extractedData) return;

      const action = extractedData.suggestedActions[actionIndex];
      // This would call the appropriate API based on action type
      // For now, we'll just mark it as applied

      return action;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchaseOrders'] });
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      queryClient.invalidateQueries({ queryKey: ['permits'] });
      toast.success('Action applied successfully');
    },
  });

  const handleAnalyze = () => {
    if (file) {
      analyzeMutation.mutate();
    }
  };

  const handleClose = () => {
    setFile(null);
    setPreview(null);
    setExtractedData(null);
    setStep('upload');
    onClose();
  };

  const handleComplete = () => {
    if (extractedData && onAnalysisComplete) {
      onAnalysisComplete(extractedData);
    }
    handleClose();
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 90) return 'text-green-600 bg-green-100';
    if (confidence >= 70) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const getDocumentTypeIcon = (type: string) => {
    const docType = DOCUMENT_TYPES.find(d => d.type === type);
    const Icon = docType?.icon || DocumentTextIcon;
    return <Icon className="w-5 h-5" />;
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="AI Document Analysis" size="xl">
      <div className="space-y-6">
        {/* Step 1: Upload */}
        {step === 'upload' && (
          <>
            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                isDragActive
                  ? 'border-primary bg-primary/5'
                  : file
                  ? 'border-green-500 bg-green-50'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
            >
              <input {...getInputProps()} />

              {file ? (
                <div className="space-y-3">
                  {preview && preview.startsWith('data:image') ? (
                    <img
                      src={preview}
                      alt="Preview"
                      className="max-h-40 mx-auto rounded-lg shadow"
                    />
                  ) : (
                    <DocumentTextIcon className="w-16 h-16 mx-auto text-gray-400" />
                  )}
                  <p className="font-medium text-gray-900">{file.name}</p>
                  <p className="text-sm text-gray-500">
                    {(file.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setFile(null);
                      setPreview(null);
                    }}
                    className="text-sm text-red-500 hover:text-red-700"
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <DocumentArrowUpIcon className="w-12 h-12 mx-auto text-gray-400" />
                  <div>
                    <p className="text-gray-900 font-medium">
                      {isDragActive ? 'Drop file here' : 'Drag & drop a document'}
                    </p>
                    <p className="text-sm text-gray-500">
                      or click to browse (PDF, Images up to 20MB)
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Supported Document Types */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">
                AI can analyze these document types:
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {DOCUMENT_TYPES.filter(d => d.type !== 'other').map((docType) => {
                  const Icon = docType.icon;
                  return (
                    <div
                      key={docType.type}
                      className="flex items-center gap-2 p-2 bg-gray-50 rounded text-sm text-gray-600"
                    >
                      <Icon className="w-4 h-4" />
                      {docType.label}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <button className="btn btn-secondary" onClick={handleClose}>
                Cancel
              </button>
              <button
                className="btn btn-primary flex items-center gap-2"
                onClick={handleAnalyze}
                disabled={!file}
              >
                <SparklesIcon className="w-4 h-4" />
                Analyze with AI
              </button>
            </div>
          </>
        )}

        {/* Step 2: Analyzing */}
        {step === 'analyzing' && (
          <div className="text-center py-12">
            <ArrowPathIcon className="w-12 h-12 mx-auto text-primary animate-spin" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">
              Analyzing Document...
            </h3>
            <p className="mt-2 text-gray-500">
              AI is extracting data from your document. This may take a moment.
            </p>
            <div className="mt-6 space-y-2 text-sm text-gray-500">
              <p>Reading document content...</p>
              <p>Identifying document type...</p>
              <p>Extracting key fields...</p>
              <p>Matching to existing records...</p>
            </div>
          </div>
        )}

        {/* Step 3: Review Results */}
        {step === 'review' && extractedData && (
          <>
            {/* Document Type & Confidence */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                {getDocumentTypeIcon(extractedData.documentType)}
                <div>
                  <p className="font-medium text-gray-900">
                    {DOCUMENT_TYPES.find(d => d.type === extractedData.documentType)?.label ||
                      'Unknown Document'}
                  </p>
                  {extractedData.matchedPO && (
                    <p className="text-sm text-gray-500">
                      Matched to PO: {extractedData.matchedPO.poNumber}
                    </p>
                  )}
                </div>
              </div>
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${getConfidenceColor(
                  extractedData.confidence
                )}`}
              >
                {extractedData.confidence}% confidence
              </span>
            </div>

            {/* Extracted Fields */}
            <div>
              <h4 className="font-medium text-gray-900 mb-3">Extracted Data</h4>
              <div className="grid grid-cols-2 gap-3">
                {Object.entries(extractedData.fields).map(([key, value]) => (
                  <div key={key} className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500 uppercase">
                      {key.replace(/_/g, ' ')}
                    </p>
                    <p className="font-medium text-gray-900 mt-1">
                      {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Warnings */}
            {extractedData.warnings.length > 0 && (
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Warnings</h4>
                <div className="space-y-2">
                  {extractedData.warnings.map((warning, index) => (
                    <div
                      key={index}
                      className={`p-3 rounded-lg flex items-start gap-3 ${
                        warning.severity === 'high'
                          ? 'bg-red-50'
                          : warning.severity === 'medium'
                          ? 'bg-yellow-50'
                          : 'bg-gray-50'
                      }`}
                    >
                      <XCircleIcon
                        className={`w-5 h-5 flex-shrink-0 ${
                          warning.severity === 'high'
                            ? 'text-red-500'
                            : warning.severity === 'medium'
                            ? 'text-yellow-500'
                            : 'text-gray-500'
                        }`}
                      />
                      <div>
                        <p className="font-medium text-sm">{warning.field}</p>
                        <p className="text-sm text-gray-600">{warning.issue}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Suggested Actions */}
            {extractedData.suggestedActions.length > 0 && (
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Suggested Actions</h4>
                <div className="space-y-2">
                  {extractedData.suggestedActions.map((action, index) => (
                    <div
                      key={index}
                      className="p-3 bg-blue-50 rounded-lg flex items-center justify-between"
                    >
                      <div className="flex items-center gap-3">
                        <CheckCircleIcon className="w-5 h-5 text-blue-500" />
                        <div>
                          <p className="font-medium text-sm text-blue-900">
                            {action.action}
                          </p>
                          <p className="text-sm text-blue-700">{action.description}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => applyActionMutation.mutate(index)}
                        className="btn btn-sm btn-primary"
                        disabled={applyActionMutation.isPending}
                      >
                        Apply
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4 border-t">
              <button className="btn btn-secondary" onClick={handleClose}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleComplete}>
                Save & Close
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
